import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, IconButton, Typography,
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import PaletteIcon from '@mui/icons-material/Palette';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import SyncIcon from '@mui/icons-material/Sync';
import StorefrontIcon from '@mui/icons-material/Storefront';

// Same palette + section icons as components/UnsavedChangesDialog — the two
// dialogs talk about the same sections, so they should look like siblings.
const SFT_BLUE = '#0071BB';
const AMBER    = '#E8920C';
const RED      = '#D9534F';
const GREEN    = '#2E9E5B';

const SECTION_ICONS = {
    pages:      ArticleIcon,
    style:      PaletteIcon,
    features:   InfoOutlinedIcon,
    processing: ShoppingBasketIcon,
    sync:       SyncIcon,
    stores:     StorefrontIcon,
};

/**
 * Group report entries that carry the same label (the three Cadeaubon parts,
 * Verwerking + klantvelden, winkels + kaart) so each place in the admin shows
 * up once. `collect` returns what to gather per entry (errors, file names).
 */
const groupBySection = (entries, sectionMeta, collect) => {
    const groups = new Map();
    for (const e of entries || []) {
        const meta = sectionMeta(e.section);
        const id = `${meta.tab}|${meta.section}`;
        if (!groups.has(id)) groups.set(id, { id, meta, items: [], blockedBy: null });
        const g = groups.get(id);
        if (e.blockedBy) g.blockedBy = e.blockedBy;
        g.items.push(...collect(e));
    }
    return Array.from(groups.values());
};

/**
 * What a Save did when it could not save everything: which sections were not
 * saved (and why), which were, and which files did not upload. Opened by
 * runSaveConfig through the screen's `setSaveReport`.
 */
const SaveReportDialog = ({ open, report, sectionMeta, onClose, onRetry, saving, t, theme }) => {
    const tr = (key, fb) => (typeof t === 'function' ? t(key, fb) : fb);
    const [showDetails, setShowDetails] = useState(false);
    useEffect(() => { if (open) setShowDetails(false); }, [open, report]);

    if (!report) return null;

    const failed   = groupBySection(report.failed, sectionMeta, (e) => e.errors || []);
    const notSent  = groupBySection(report.warnings, sectionMeta, (w) => w.files || []);
    const saved    = groupBySection((report.saved || []).map(section => ({ section })), sectionMeta, () => []);
    const hasFailed = failed.length > 0;
    // Red = something was not saved. Amber = saved, but a file did not make it.
    const accent = hasFailed ? RED : AMBER;
    const HeadIcon = hasFailed ? ErrorOutlineRoundedIcon : ReportProblemRoundedIcon;

    const title = report.unexpected
        ? tr('save_report_title_unexpected', 'Opslaan is onderbroken')
        : hasFailed
            ? tr('save_report_title_failed', 'Niet alles is opgeslagen')
            : tr('save_report_title_warnings', 'Opgeslagen, maar niet volledig');
    const intro = report.unexpected
        ? tr('save_report_intro_unexpected', 'Er ging onverwacht iets mis tijdens het opslaan. Het is niet zeker wat de webshop bereikt heeft. Herlaad de pagina om de huidige toestand te zien voor je opnieuw opslaat.')
        : hasFailed
            ? tr('save_report_intro_failed', 'De onderdelen hieronder zijn niet opgeslagen. Je wijzigingen staan nog in het scherm en blijven als niet-opgeslagen gemarkeerd, zodat je ze opnieuw kunt opslaan.')
            : tr('save_report_intro_warnings', 'Alles is opgeslagen, behalve de bestanden hieronder. Kies ze opnieuw en sla nog eens op.');
    const pill = hasFailed && !report.unexpected
        ? `${failed.length} ${failed.length === 1
            ? tr('save_report_count_failed_one', 'onderdeel niet opgeslagen')
            : tr('save_report_count_failed_many', 'onderdelen niet opgeslagen')}`
        : '';

    const isDark = theme === 'dark';
    const paperBg    = isDark ? '#222b37' : '#ffffff';
    const textMain   = isDark ? '#F9FAFB' : '#1F2937';
    const textMuted  = isDark ? 'rgba(249,250,251,0.62)' : 'rgba(31,41,55,0.60)';
    const hairline   = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
    const rgba       = (hex, a) => {
        const n = parseInt(hex.slice(1), 16);
        return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
    };
    const headTint   = `linear-gradient(180deg, ${rgba(accent, isDark ? 0.16 : 0.10)} 0%, ${rgba(accent, 0)} 100%)`;

    const sectionRow = (g, tone, body) => {
        const Icon = SECTION_ICONS[g.meta.icon] || EditOutlinedIcon;
        return (
            <Box key={g.id} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.25,
                px: 1.25, py: 1,
                borderRadius: 2,
                backgroundColor: rgba(tone, isDark ? 0.10 : 0.05),
                border: `1px solid ${rgba(tone, isDark ? 0.32 : 0.22)}`,
            }}>
                <Box sx={{
                    flexShrink: 0, width: 30, height: 30, borderRadius: '9px',
                    display: 'grid', placeItems: 'center',
                    backgroundColor: rgba(tone, isDark ? 0.24 : 0.12), color: tone,
                }}>
                    <Icon sx={{ fontSize: 17 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {g.meta.tab && g.meta.section ? (
                        <Typography sx={{
                            fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase', color: textMuted, lineHeight: 1.2,
                        }}>
                            {g.meta.tab}
                        </Typography>
                    ) : null}
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: textMain, lineHeight: 1.3 }}>
                        {g.meta.section || g.meta.tab}
                    </Typography>
                    {body}
                </Box>
            </Box>
        );
    };

    const reasonText = (g) => {
        if (g.blockedBy === 'containers') {
            return tr('save_report_blocked_containers', 'Niet opgeslagen omdat een productcontainer niet aangemaakt kon worden. Sla opnieuw op zodra dat gelukt is.');
        }
        const first = g.items.find(er => er.message);
        const count = g.items.length;
        const head = count > 1
            ? `${count} ${tr('save_report_requests_many', 'aanvragen mislukt')}`
            : tr('save_report_requests_one', '1 aanvraag mislukt');
        return `${head}: ${first ? first.message : tr('save_report_no_message', 'geen foutmelding ontvangen')}`;
    };
    const hasDetails = failed.some(g => g.items.some(er => er.path));

    return (
        // NB: no className={theme} on <Dialog> itself — see UnsavedChangesDialog.
        <Dialog
            open={!!open}
            onClose={() => !saving && onClose && onClose()}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    className: theme,
                    sx: {
                        position: 'relative', overflow: 'hidden', borderRadius: 3.5,
                        backgroundColor: paperBg, backgroundImage: 'none',
                        border: `1px solid ${hairline}`,
                        boxShadow: isDark ? '0 24px 60px -12px rgba(0,0,0,0.7)' : '0 24px 60px -16px rgba(16,24,40,0.28)',
                    },
                },
                backdrop: {
                    sx: { backgroundColor: isDark ? 'rgba(8,11,16,0.62)' : 'rgba(16,24,40,0.42)', backdropFilter: 'blur(3px)' },
                },
            }}
        >
            <Box sx={{ height: 4, backgroundColor: accent }} />

            <IconButton
                onClick={() => onClose && onClose()}
                disabled={saving}
                aria-label={tr('save_report_close', 'Sluiten')}
                size="small"
                sx={{
                    position: 'absolute', top: 10, right: 10, zIndex: 1, color: textMuted,
                    '&:hover': { color: textMain, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                }}
            >
                <CloseRoundedIcon fontSize="small" />
            </IconButton>

            <Box sx={{ px: 3, pt: 3, pb: 2, background: headTint, display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
                <Box sx={{
                    flexShrink: 0, width: 46, height: 46, borderRadius: '14px',
                    display: 'grid', placeItems: 'center',
                    backgroundColor: rgba(accent, isDark ? 0.18 : 0.13), color: accent,
                    boxShadow: `0 0 0 1px ${rgba(accent, isDark ? 0.30 : 0.22)} inset`,
                }}>
                    <HeadIcon />
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25, pr: 3 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.12rem', lineHeight: 1.25, color: textMain, letterSpacing: '-0.01em' }}>
                        {title}
                    </Typography>
                    {pill && (
                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', mt: 0.6, px: 1, py: 0.2, borderRadius: 999,
                            backgroundColor: rgba(accent, isDark ? 0.18 : 0.13), color: accent,
                            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.01em',
                        }}>
                            {pill}
                        </Box>
                    )}
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, pt: 0.5, pb: 1.5 }}>
                <Typography sx={{ fontSize: '0.875rem', color: textMuted, lineHeight: 1.5, mb: 1.75 }}>
                    {intro}
                </Typography>

                {hasFailed && (
                    <Box data-save-report="failed" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {failed.map(g => sectionRow(g, RED, (
                            <>
                                <Typography sx={{ fontSize: '0.78rem', color: textMuted, lineHeight: 1.45, mt: 0.35, overflowWrap: 'anywhere' }}>
                                    {reasonText(g)}
                                </Typography>
                                <Collapse in={showDetails} unmountOnExit>
                                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                        {g.items.filter(er => er.path).map((er, i) => (
                                            <Typography key={i} sx={{
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                                fontSize: '0.7rem', color: textMuted, lineHeight: 1.4, overflowWrap: 'anywhere',
                                            }}>
                                                {er.method} {er.path}{er.message ? ` — ${er.message}` : ''}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Collapse>
                            </>
                        )))}
                        {hasDetails && (
                            <Button
                                onClick={() => setShowDetails(v => !v)}
                                size="small"
                                endIcon={<ExpandMoreRoundedIcon sx={{ transition: 'transform 160ms', transform: showDetails ? 'rotate(180deg)' : 'none' }} />}
                                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: textMuted, px: 0.75 }}
                            >
                                {showDetails
                                    ? tr('save_report_details_hide', 'Verberg technische details')
                                    : tr('save_report_details_show', 'Technische details')}
                            </Button>
                        )}
                    </Box>
                )}

                {notSent.length > 0 && (
                    <Box data-save-report="warnings" sx={{ mt: hasFailed ? 2 : 0 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: textMuted, mb: 0.75 }}>
                            {tr('save_report_not_uploaded', 'Niet geüpload')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {notSent.map(g => sectionRow(g, AMBER, (
                                <Typography sx={{ fontSize: '0.78rem', color: textMuted, lineHeight: 1.45, mt: 0.35, overflowWrap: 'anywhere' }}>
                                    {g.items.join(', ')}
                                </Typography>
                            )))}
                        </Box>
                    </Box>
                )}

                {hasFailed && saved.length > 0 && (
                    <Box data-save-report="saved" sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: textMuted, mb: 0.75 }}>
                            {tr('save_report_saved_title', 'Wel opgeslagen')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                            {saved.map(g => (
                                <Box key={g.id} sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    px: 1, py: 0.35, borderRadius: 999,
                                    backgroundColor: rgba(GREEN, isDark ? 0.18 : 0.10),
                                    color: isDark ? '#7FD6A4' : '#1F7A45',
                                    fontSize: '0.75rem', fontWeight: 600,
                                }}>
                                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                                    {g.meta.section || g.meta.tab}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <Button
                    onClick={() => onClose && onClose()}
                    disabled={saving}
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', px: 2, py: 0.8, borderRadius: 2,
                        color: isDark ? '#6CB8E6' : SFT_BLUE, border: `1px solid ${hairline}`,
                        '&:hover': { backgroundColor: 'rgba(0,113,187,0.06)', borderColor: SFT_BLUE },
                    }}
                >
                    {tr('save_report_close', 'Sluiten')}
                </Button>
                {hasFailed && typeof onRetry === 'function' && (
                    <Button
                        onClick={onRetry}
                        variant="contained"
                        disableElevation
                        autoFocus
                        disabled={saving}
                        startIcon={saving
                            ? <CircularProgress size={15} thickness={5} sx={{ color: '#fff' }} />
                            : <ReplayRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2.25, py: 0.8, borderRadius: 2,
                            backgroundColor: SFT_BLUE, color: '#fff',
                            '&:hover': { backgroundColor: '#005c99' },
                        }}
                    >
                        {tr('save_report_retry', 'Opnieuw proberen')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

SaveReportDialog.propTypes = {
    open:        PropTypes.bool,
    report:      PropTypes.object,
    sectionMeta: PropTypes.func.isRequired,
    onClose:     PropTypes.func,
    onRetry:     PropTypes.func,
    saving:      PropTypes.bool,
    t:           PropTypes.func,
    theme:       PropTypes.string,
};

export default SaveReportDialog;
